import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";

import CartBackImg from "../assets/images/cart-back.png";
import BlogPageShell from "../components/blog/BlogPageShell";
import BlogDetailSkeleton from "../components/blog/BlogDetailSkeleton";
import {
  getBlogBanner,
  getBlogDate,
  getRelatedPosts,
} from "../utils/blogDisplayUtils";

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const previewPost = useMemo(
    () => post || posts.find((entry) => entry.slug === slug) || null,
    [post, posts, slug]
  );

  const relatedPosts = useMemo(
    () => getRelatedPosts(posts, previewPost),
    [posts, previewPost]
  );

  const banner = useMemo(() => getBlogBanner(previewPost), [previewPost]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
    const loadData = async () => {
      if (!slug) {
        setError("Blog post not found.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const baseUrl = import.meta.env.VITE_APP_BASE_URL;
        const shouldRefreshList = posts.length === 0;

        const requests = [
          axios.get(`${baseUrl}/api/blogs/${slug}`),
          ...(shouldRefreshList
            ? [axios.get(`${baseUrl}/api/blogs/public`)]
            : []),
        ];

        const [postResponse, listResponse] = await Promise.all(requests);

        setPost(postResponse?.data?.data ?? null);

        if (listResponse) {
          const items = listResponse?.data?.data;
          setPosts(Array.isArray(items) ? items : []);
        }
      } catch {
        setError("Unable to load this blog post.");
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [slug]);

  const showLayout = Boolean(slug) && !error;

  return (
    <Fragment>
      <div className="banner-section cart-banner contact">
        <img src={CartBackImg} alt="Blog banner" />
        <p className="banner-page-title">
          <span>Blog</span>
        </p>
      </div>

      <div className="contact-sec blog-sec">
        <div className="container">
          <div className="top-property-button mb-4">
            <Link to="/blog">← Back to Blog</Link>
          </div>

          {error ? <p className="text-center">{error}</p> : null}

          {showLayout ? (
            <BlogPageShell
              posts={relatedPosts}
              activeSlug={slug}
              sidebarTitle="Related Posts"
            >
              <div className="blog-detail-stage">
                {loading ? <BlogDetailSkeleton /> : null}
                {!loading && post ? (
                  <article
                    key={slug}
                    className="blog-detail-article blog-detail-enter"
                  >
                    <h1 className="blog-detail-title">{post.title}</h1>
                    <div className="blog-detail-meta">
                      {getBlogDate(post) ? (
                        <span>
                          <i className="fa fa-calendar-o" aria-hidden="true" />{" "}
                          {getBlogDate(post)}
                        </span>
                      ) : null}
                    </div>
                    {banner ? (
                      <img
                        src={banner}
                        alt=""
                        className="blog-detail-banner"
                      />
                    ) : null}
                    <div
                      className="blog-detail-content"
                      dangerouslySetInnerHTML={{ __html: post.content || "" }}
                    />
                  </article>
                ) : null}
              </div>
            </BlogPageShell>
          ) : null}
        </div>
      </div>
    </Fragment>
  );
};

export default BlogDetail;
